import { useState } from 'react';
import { useEditInsurance } from './useEditInsurance';
import { useMedicareForm } from './useMedicareForm';
import { useOtherInsuranceForm } from './useOtherInsuranceForm';
import { useInsuranceStore } from '@store';

export const useEditInsuranceModal = (patient_id: string) => {
  const {
    handleForceClose,
    handleOpen,
    isOpen,
    tabIndex,
    requestScreenSwitch,
    confirmScreenSwitch,
    cancelScreenSwitch,
    pendingSwitch,
    isMedicareFilled,
    isPrimaryOtherFilled,
    isSecondaryFilled,
  } = useEditInsurance(patient_id);

  const medicare = useMedicareForm(patient_id);
  const primaryOther = useOtherInsuranceForm(patient_id, 'primary_other');
  const secondary = useOtherInsuranceForm(patient_id, 'secondary');

  // Save modal open state per screen
  const [medicareSaveModalOpen, setMedicareSaveModalOpen] = useState(false);
  const [primaryOtherSaveModalOpen, setPrimaryOtherSaveModalOpen] =
    useState(false);
  const [secondarySaveModalOpen, setSecondarySaveModalOpen] = useState(false);
  const [closeSaveModalOpen, setCloseSaveModalOpen] = useState(false);

  const currentScreen =
    tabIndex === '1'
      ? 'medicare'
      : tabIndex === '2'
        ? 'primary_other'
        : 'secondary';

  const currentIsDirty =
    currentScreen === 'medicare'
      ? medicare.isDirty
      : currentScreen === 'primary_other'
        ? primaryOther.isDirty
        : secondary.isDirty;

  const { primaryOtherHasOverlap, secondaryHasOverlap } = useInsuranceStore();

  const currentIsValid =
    currentScreen === 'medicare'
      ? medicare.isValid
      : currentScreen === 'primary_other'
        ? primaryOther.isValid && !primaryOtherHasOverlap
        : secondary.isValid && !secondaryHasOverlap;

  const currentIsSubmitting =
    medicare.isSubmitting ||
    primaryOther.isSubmitting ||
    secondary.isSubmitting;

  const handleClose = () => {
    if (currentIsDirty && currentIsValid) {
      setCloseSaveModalOpen(true);
    } else {
      handleForceClose();
    }
  };

  // Tab switch
  const handleChangeTab = (_: React.SyntheticEvent, newValue: string) => {
    const switched = requestScreenSwitch(
      newValue,
      currentIsDirty && currentIsValid,
    );
    if (switched) {
      if (currentScreen === 'medicare') setMedicareSaveModalOpen(true);
      else if (currentScreen === 'primary_other')
        setPrimaryOtherSaveModalOpen(true);
      else setSecondarySaveModalOpen(true);
    }
  };

  const afterSaveOrDiscard = () => {
    if (pendingSwitch !== null) confirmScreenSwitch();
    if (closeSaveModalOpen) handleForceClose();
  };

  // --- Medicare save modal ---
  const handleMedicareSave = async () => {
    await medicare.submit();
    setMedicareSaveModalOpen(false);
    afterSaveOrDiscard();
  };
  const handleMedicareDiscard = () => {
    medicare.resetForm();
    setMedicareSaveModalOpen(false);
    afterSaveOrDiscard();
  };
  const handleMedicareModalClose = () => {
    setMedicareSaveModalOpen(false);
    cancelScreenSwitch();
    setCloseSaveModalOpen(false);
  };

  // --- Primary other save modal ---
  const handlePrimaryOtherSave = async () => {
    await primaryOther.submit();
    setPrimaryOtherSaveModalOpen(false);
    afterSaveOrDiscard();
  };
  const handlePrimaryOtherDiscard = () => {
    primaryOther.resetForm();
    setPrimaryOtherSaveModalOpen(false);
    afterSaveOrDiscard();
  };
  const handlePrimaryOtherModalClose = () => {
    setPrimaryOtherSaveModalOpen(false);
    cancelScreenSwitch();
    setCloseSaveModalOpen(false);
  };

  // --- Secondary save modal ---
  const handleSecondarySave = async () => {
    await secondary.submit();
    setSecondarySaveModalOpen(false);
    afterSaveOrDiscard();
  };
  const handleSecondaryDiscard = () => {
    secondary.resetForm();
    setSecondarySaveModalOpen(false);
    afterSaveOrDiscard();
  };
  const handleSecondaryModalClose = () => {
    setSecondarySaveModalOpen(false);
    cancelScreenSwitch();
    setCloseSaveModalOpen(false);
  };

  // --- Close modal (triggered when X is pressed with dirty state) ---
  const handleCloseSave = async () => {
    if (currentScreen === 'medicare') await medicare.submit();
    else if (currentScreen === 'primary_other') await primaryOther.submit();
    else await secondary.submit();
    setCloseSaveModalOpen(false);
    handleForceClose();
  };
  const handleCloseDiscard = () => {
    if (currentScreen === 'medicare') medicare.resetForm();
    else if (currentScreen === 'primary_other') primaryOther.resetForm();
    else secondary.resetForm();
    setCloseSaveModalOpen(false);
    handleForceClose();
  };

  // Submit for the Save button in the footer
  const handleSaveCurrentScreen = async () => {
    if (currentScreen === 'medicare') await medicare.submit();
    else if (currentScreen === 'primary_other') await primaryOther.submit();
    else await secondary.submit();
  };

  return {
    isOpen,
    handleOpen,
    handleClose,
    tabIndex,
    handleChangeTab,
    isMedicareFilled,
    isPrimaryOtherFilled,
    isSecondaryFilled,
    // form methods per screen (for FormProvider)
    medicareFormMethods: medicare.formMethods,
    primaryOtherFormMethods: primaryOther.formMethods,
    secondaryFormMethods: secondary.formMethods,
    // footer save button
    handleSaveCurrentScreen,
    currentIsDirty,
    currentIsValid,
    currentIsSubmitting,
    currentScreen,
    // save modals
    medicareSaveModalOpen,
    primaryOtherSaveModalOpen,
    secondarySaveModalOpen,
    closeSaveModalOpen,
    handleMedicareSave,
    handleMedicareDiscard,
    handleMedicareModalClose,
    handlePrimaryOtherSave,
    handlePrimaryOtherDiscard,
    handlePrimaryOtherModalClose,
    handleSecondarySave,
    handleSecondaryDiscard,
    handleSecondaryModalClose,
    handleCloseSave,
    handleCloseDiscard,
    handleCloseModalClose: () => {
      setCloseSaveModalOpen(false);
      cancelScreenSwitch();
    },
    // per-entry save
    handleSubmitPrimaryOtherEntry: primaryOther.submitEntry,
    handleSubmitSecondaryEntry: secondary.submitEntry,
    handleSubmitMedicare: medicare.submit,
    // delete (shared)
    handleDeleteEntry: primaryOther.deleteEntry,
    handleDeleteSecondaryEntry: secondary.deleteEntry,
    handleResetPrimaryOther: primaryOther.resetForm,
    handleResetSecondary: secondary.resetForm,
    // clear medicare
    handleClearMedicare: medicare.clearEntry,
  };
};
